import * as React from 'react';
import { useIntl } from 'react-intl';

import placements from '../../placements';
import { parseCustomMetaLanguage } from '../../utils/custom-meta-language';
import { setFrequencyCapping } from '../../utils/frequency-capping';
import { openURLbyTarget } from '../../utils/open-url-by-target';
import type { TrackingLibrary } from '../../utils/tracking';
import { trackEvent } from '../../utils/tracking';
import withBraze from '../../utils/with-braze';
import CustomPrescreen from '../prescreen-consumer/custom-prescreen';
import type { BrazeCustomEvent } from '../prescreen-consumer/prescreen-consumer';

import { Styled } from './pop-over-consumer.styles';

const PLACEMENT = placements.POP_OVER;

export interface PopOverPayload {
  campaignId: string;
  message?: string;
  title?: string;
}

export interface PopOverProps {
  close: () => void;
  payload: PopOverPayload;
  section: string;
  trackingLibrary?: TrackingLibrary;
}

type ViewportStyle = React.CSSProperties & {
  '--pop-over-viewport-height'?: string;
  '--pop-over-viewport-top'?: string;
};

const PopOver: React.FC<PopOverProps> = ({
  close,
  payload: { campaignId, message, title },
  section,
  trackingLibrary,
}) => {
  const intl = useIntl();
  const metaLanguage = React.useMemo(
    () => parseCustomMetaLanguage(message),
    [message]
  );
  const [viewportStyle, setViewportStyle] = React.useState<ViewportStyle>({});

  React.useEffect(() => {
    const viewport = window.visualViewport;
    if (!metaLanguage || !viewport) return;

    // Mobile keyboards can resize and pan the visible area without resizing
    // the layout viewport used by fixed positioning and viewport units.
    const updateViewport = (): void => {
      setViewportStyle({
        '--pop-over-viewport-height': `${viewport.height}px`,
        '--pop-over-viewport-top': `${viewport.offsetTop}px`,
      });
    };

    updateViewport();
    viewport.addEventListener('resize', updateViewport);
    viewport.addEventListener('scroll', updateViewport);

    return () => {
      viewport.removeEventListener('resize', updateViewport);
      viewport.removeEventListener('scroll', updateViewport);
    };
  }, [metaLanguage]);

  const handleClose = (): void => {
    trackEvent('brazeClose', {
      brazePlacement: PLACEMENT,
      brazeTriggerID: campaignId,
    });
    close();
  };

  const handleURLButtonClick = (
    url: string,
    target: '_self' | '_blank' = '_blank',
    buttonId = 'pop-over-button'
  ): void => {
    trackingLibrary?.trackInAppMessageHTMLClick(
      PLACEMENT,
      campaignId,
      buttonId
    );
    openURLbyTarget(url, target);
  };

  const sendBrazeCustomEvent = (payload: BrazeCustomEvent): void => {
    trackingLibrary?.sendBrazeCustomEvent(payload);
  };

  React.useEffect(() => {
    if (!metaLanguage) return;

    trackingLibrary?.trackInAppMessageImpression(PLACEMENT, campaignId);
    setFrequencyCapping(section, PLACEMENT);
  }, [campaignId, metaLanguage, section, trackingLibrary]);

  const ariaLabel =
    metaLanguage?.title ||
    title ||
    intl.formatMessage({
      id: 'BRAZE_POP_OVER_A11Y',
      defaultMessage: 'Registration',
    });

  if (!metaLanguage) return null;

  return (
    <Styled.PopOver
      aria-label={ariaLabel}
      data-qa="braze-pop-over"
      isStatic
      show
      style={viewportStyle}
    >
      <Styled.CloseButton onClick={handleClose} size="large" />
      <Styled.Content data-qa="braze-pop-over-content">
        <CustomPrescreen
          key={campaignId}
          content={metaLanguage.content}
          successContent={metaLanguage.successContent}
          onClose={handleClose}
          onURLButtonClick={handleURLButtonClick}
          sendBrazeCustomEvent={sendBrazeCustomEvent}
        />
      </Styled.Content>
    </Styled.PopOver>
  );
};

const PopOverConsumer = withBraze(PLACEMENT)(PopOver);

export { PopOverConsumer, PopOver };
